import { useEffect } from 'react';

/**
 * When `locked` is true, prevents the page body from scrolling underneath an
 * open modal/panel. Restores previous overflow on cleanup or when locked
 * becomes false.
 *
 * Counts nested locks so multiple stacked modals don't unlock prematurely.
 */
let lockCount = 0;
let prevOverflow: string | null = null;
let prevPaddingRight: string | null = null;

function applyLock(): void {
  lockCount += 1;
  if (lockCount > 1) return;
  prevOverflow = document.body.style.overflow;
  prevPaddingRight = document.body.style.paddingRight;
  // Compensate for scrollbar disappearance to avoid layout shift
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
  document.body.style.overflow = 'hidden';
}

function releaseLock(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.style.overflow = prevOverflow ?? '';
    document.body.style.paddingRight = prevPaddingRight ?? '';
    prevOverflow = null;
    prevPaddingRight = null;
  }
}

export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    applyLock();
    return () => releaseLock();
  }, [locked]);
}
